import useModal from '@/hooks/use-modals';
import errorSign from '@/assets/images/error-sign.svg';
import { Box, Typography, Button, Link, Stack, Card as MuiCard } from '@mui/material';
import { ModalDialog } from './DynamicUI/ModalDialog';
import { ErrorResponse } from '@/schemas/errorResponseSchema';
import { useMemo } from 'react';
import { Card } from './DynamicUI/Card';
import { CardBorderRadius, CardShadows } from '@/constants/cardTypes';
import { ComponentsTypes } from '@/constants/componentsTypes';
import { CardContent } from './DynamicUI/CardContent';
import { CardContentPadding } from '@/constants/cardContentTypes';
import { TypographyColors } from '@/constants/typographyTypes';
import { CenterColumnLayout } from './SceneWrap';

const SUPPORT_EMAIL = 'support@joinroot.com';

export type ErrorType = 'general' | 'unauthorized' | 'development';

interface ErrorAction {
  label: string;
  onClick: () => void;
  variant?: 'contained' | 'outlined' | 'text';
}

interface ErrorConfig {
  title: string;
  message: string;
  primaryAction: ErrorAction;
  secondaryAction?: ErrorAction;
}

interface ErrorProps {
  errorData?: ErrorResponse;
  errorStatus?: number | null;
}

export function Error({ errorData, errorStatus }: ErrorProps) {
  const { getModal } = useModal();

  const errorType = useMemo((): ErrorType => {
    if (errorStatus === 401) return 'unauthorized';
    if (errorData?.stack_trace) return 'development';
    return 'general';
  }, [errorStatus, errorData]);

  const helpModalId = `error-help-modal-${errorType}`;

  const reloadPage = () => window.location.reload();
  const emailSupport = () => window.location.assign(`mailto:${SUPPORT_EMAIL}`);

  const handleHelpClick = () => {
    const modal = getModal(helpModalId);
    modal?.open();
  };

  const handleHelpModalClose = () => {
    const modal = getModal(helpModalId);
    modal?.close();
  };

  const ERROR_CONFIGS: Record<Exclude<ErrorType, 'development'>, ErrorConfig> = {
    general: {
      title: 'Hmm, something went wrong.',
      message: 'We ran into a problem on our side. Please try again.',
      primaryAction: {
        label: 'Reload page',
        onClick: reloadPage,
        variant: 'contained',
      },
      secondaryAction: {
        label: 'Need help?',
        onClick: handleHelpClick,
        variant: 'text',
      },
    },
    unauthorized: {
      title: "Oops, that link didn't work.",
      message:
        'It looks like the link you used is expired. Please try again, or reach out to customer support if the issue persists.',
      primaryAction: {
        label: 'Email customer support',
        onClick: emailSupport,
        variant: 'contained',
      },
    },
  };

  if (errorType === 'development') {
    return (
      <CenterColumnLayout
        columnWidth='wide'
        slotMain={
          <DevelopmentError
            errorData={errorData}
            errorStatus={errorStatus}
          />
        }
      ></CenterColumnLayout>
    );
  }

  const config = ERROR_CONFIGS[errorType];

  return (
    <CenterColumnLayout
      slotMain={
        <>
          <Card
            component={{
              type: ComponentsTypes.CARD,
              border: true,
              border_radius: CardBorderRadius.LARGE,
              shadow: CardShadows.NONE,
              components: [],
            }}
          >
            <CardContent
              component={{
                type: ComponentsTypes.CARD_CONTENT,
                padding: CardContentPadding.LARGE,
                components: [],
              }}
            >
              <Stack spacing={5}>
                <Stack spacing={1}>
                  <Box
                    component='img'
                    alt='error indicator'
                    src={errorSign}
                  />
                  <Typography variant='h2'>{config.title}</Typography>
                  <Typography
                    variant='body1'
                    color={TypographyColors.TEXT_SECONDARY}
                  >
                    {config.message}
                  </Typography>
                </Stack>

                {errorData?.error_id && <ErrorCard errorId={errorData.error_id} />}

                <Stack spacing={2}>
                  <Button
                    variant={config.primaryAction.variant}
                    size='large'
                    onClick={config.primaryAction.onClick}
                  >
                    {config.primaryAction.label}
                  </Button>
                  {config.secondaryAction && (
                    <Button
                      variant={config.secondaryAction.variant}
                      size='large'
                      onClick={config.secondaryAction.onClick}
                    >
                      {config.secondaryAction.label}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <ModalDialog
            id={helpModalId}
            status='closed'
          >
            <Stack spacing={5}>
              <Stack spacing={1}>
                <Typography variant='h2'>Need help?</Typography>
                <Typography
                  variant='body1'
                  color={TypographyColors.TEXT_SECONDARY}
                >
                  Please contact{' '}
                  <Link
                    href={`mailto:${SUPPORT_EMAIL}`}
                    color={TypographyColors.TEXT_SECONDARY}
                    sx={{ textDecoration: 'underline' }}
                  >
                    {SUPPORT_EMAIL}
                  </Link>{' '}
                  if you need assistance{errorData?.error_id ? ' and include this error code:' : '.'}
                </Typography>
              </Stack>

              {errorData?.error_id && <ErrorCard errorId={errorData.error_id} />}

              <Stack spacing={2}>
                <Button
                  variant='contained'
                  size='large'
                  onClick={emailSupport}
                >
                  Email customer support
                </Button>
                <Button
                  variant='outlined'
                  size='large'
                  onClick={handleHelpModalClose}
                  sx={{ color: 'text.primary', borderColor: 'primary.main' }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </>
      }
    ></CenterColumnLayout>
  );
}

interface ErrorCardProps {
  errorClass?: string;
  errorId: string;
  errorMessage?: string;
}

function ErrorCard({ errorClass, errorId, errorMessage }: ErrorCardProps) {
  return (
    <Card
      component={{
        type: ComponentsTypes.CARD,
        border: true,
        border_radius: CardBorderRadius.MEDIUM,
        shadow: CardShadows.NONE,
        components: [],
      }}
    >
      <CardContent
        component={{
          type: ComponentsTypes.CARD_CONTENT,
          padding: CardContentPadding.SMALL,
          components: [],
        }}
      >
        {errorClass && errorMessage && (
          <>
            <Typography variant='h4'>Error Message:</Typography>
            <Typography
              variant='body1'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {errorClass}
            </Typography>
            {errorMessage.split('\n').map((errorLine) => (
              <Typography
                variant='body1'
                color={TypographyColors.TEXT_SECONDARY}
              >
                {errorLine}
              </Typography>
            ))}
          </>
        )}

        <Typography variant='h4'>Error code:</Typography>
        <Typography
          variant='body1'
          color={TypographyColors.TEXT_SECONDARY}
        >
          {errorId}
        </Typography>
      </CardContent>
    </Card>
  );
}

function DevelopmentError({ errorData, errorStatus }: ErrorProps) {
  const reloadPage = () => window.location.reload();

  return (
    <MuiCard
      variant='outlined'
      sx={{ '--scene-max-width': '95vw', borderRadius: 'var(--border-radius-large)', marginBottom: 8 }}
    >
      <CardContent
        component={{
          type: ComponentsTypes.CARD_CONTENT,
          padding: CardContentPadding.LARGE,
          components: [],
        }}
      >
        <Stack spacing={5}>
          <Stack spacing={1}>
            <Typography variant='h2'>Development Error</Typography>
            <Typography
              variant='body1'
              color={TypographyColors.TEXT_SECONDARY}
            >
              An error occurred with status code {errorStatus}. Stack trace provided below.
            </Typography>
          </Stack>

          <Button
            variant='contained'
            size='large'
            onClick={reloadPage}
            sx={{ maxWidth: '260px' }}
          >
            Reload page
          </Button>

          {errorData?.error_id && (
            <ErrorCard
              errorClass={errorData.error_class}
              errorId={errorData.error_id}
              errorMessage={errorData.error_message}
            />
          )}

          {errorData?.stack_trace && (
            <MuiCard variant='outlined'>
              <Box
                component='pre'
                sx={{
                  fontSize: '0.75rem',
                  overflowX: 'auto',
                  padding: 3,
                  margin: 0,
                }}
              >
                {errorData.stack_trace.join('\n')}
              </Box>
            </MuiCard>
          )}
        </Stack>
      </CardContent>
    </MuiCard>
  );
}
